// ------------------------------------------------------------------------ //
// TanStack Query hooks for Memo
// ------------------------------------------------------------------------ //
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@shared/api/api';
import Auth from '@shared/services/Auth';
import dayjs from 'dayjs';

// ------------------------------------------------------------------------ //
// Query Keys
// ------------------------------------------------------------------------ //
export const memoKeys = {
  all: ['memo'],
  user: (userId) => [...memoKeys.all, 'user', userId],
};

// ------------------------------------------------------------------------ //
// Get User Memo
// ------------------------------------------------------------------------ //
export function useUserMemo() {
  const { user } = Auth.user() || { user: {} };

  return useQuery({
    queryKey: memoKeys.user(user.id),
    queryFn: async () => {
      return api.getUserMemo(user.id);
    },
    enabled: !!user.id,
    staleTime: 30000,
  });
}

// ------------------------------------------------------------------------ //
// Create User Memo Mutation
// ------------------------------------------------------------------------ //
export function useCreateUserMemo() {
  const queryClient = useQueryClient();
  const { user } = Auth.user() || { user: {} };

  return useMutation({
    mutationFn: async ({ date, description }) => {
      // La date vient d'un input datetime-local, elle est déjà dans la timezone de l'utilisateur
      // On la parse simplement et on la convertit en timestamp Unix
      const response = await api.createUserMemo(
        user.id,
        dayjs(date).unix(),
        description
      );
      return response.data;
    },
    onSuccess: (newMemo) => {
      // Optimistically update the cache
      queryClient.setQueryData(memoKeys.user(user.id), (old) => {
        return old ? [...old, newMemo] : [newMemo];
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries(memoKeys.user(user.id));
    },
  });
}

// ------------------------------------------------------------------------ //
// Update User Memo Mutation
// ------------------------------------------------------------------------ //
export function useUpdateUserMemo() {
  const queryClient = useQueryClient();
  const { user } = Auth.user() || { user: {} };

  return useMutation({
    mutationFn: async ({ id, date, description }) => {
      // La date vient d'un input datetime-local, elle est déjà dans la timezone de l'utilisateur
      // On la parse simplement et on la convertit en timestamp Unix
      const response = await api.updateUserMemo(
        id,
        dayjs(date).unix(),
        description
      );
      return response.data;
    },
    onSuccess: (updatedMemo) => {
      // Optimistically update the cache
      queryClient.setQueryData(memoKeys.user(user.id), (old) => {
        if (!old) return [updatedMemo];
        return old.map((memo) => (memo.id === updatedMemo.id ? updatedMemo : memo));
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries(memoKeys.user(user.id));
    },
  });
}

// ------------------------------------------------------------------------ //
// Delete User Memo Mutation
// ------------------------------------------------------------------------ //
export function useDeleteUserMemo() {
  const queryClient = useQueryClient();
  const { user } = Auth.user() || { user: {} };

  return useMutation({
    mutationFn: async (memoId) => {
      await api.deleteUserMemo(memoId);
      return memoId;
    },
    onSuccess: (deletedId) => {
      // Optimistically update the cache
      queryClient.setQueryData(memoKeys.user(user.id), (old) => {
        if (!old) return [];
        return old.filter((memo) => memo.id !== deletedId);
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries(memoKeys.user(user.id));
    },
  });
}
